import $ from 'jquery';

import GoogleAnalyticsUtils from 'chairisher/util/googleanalytics';

import { getBingEcommPageType } from 'chairisher/analytics/bing';
import { getAmplitudeEventPropertiesForProduct } from 'chairisher/analytics/product';
import { logRedditEvent } from 'chairisher/analytics/reddit';
import { getEmail } from 'chairisher/context/account';
import { getAdwordsAddToCartEventLabel } from 'chairisher/context/tracking';
import { trackAddToCartForPRT } from 'chairisher/thirdparty/pinterestretargeting';
import {
    AmplitudeEventProperty,
    CheckoutPosition,
    logAmplitudeButtonClickEvent,
    logAmplitudeClickEvent,
    logAmplitudeEvent,
    trackAdwordsConversion,
} from 'chairisher/util/analytics';

/**
 * Tracks clicks on the mini cart "checkout" button
 */
export function trackCheckoutButton() {
    logAmplitudeButtonClickEvent('checkout', { [AmplitudeEventProperty.POSITION]: CheckoutPosition.MINI_CART });
}

/**
 * Tracks adding a product to the cart
 *
 * @param {ProductModel} product The product that is being added to the cart
 * @param {string=} position An optional string indicating the location of the add to cart click
 * @param {number=} productQuantity The number of units added in this action
 * @param {Object=} additionalAmplitudeProps An optional object containing additional Amplitude event properties
 */
export function trackAddToCart(product, position, productQuantity, additionalAmplitudeProps = {}) {
    const productId = product.getId();
    const productPrice = product.getPrice();
    const quantity = productQuantity ?? product.getQuantity();
    const totalValue = productPrice * quantity;

    trackAdwordsConversion(getAdwordsAddToCartEventLabel(), totalValue);

    const amplitudeProps = {
        ...getAmplitudeEventPropertiesForProduct(product),
        ...additionalAmplitudeProps,
    };
    if (position) {
        amplitudeProps[AmplitudeEventProperty.POSITION] = position;
    }
    logAmplitudeEvent('cart - add', amplitudeProps);

    GoogleAnalyticsUtils.logEvent('add_to_cart', {
        currency: 'USD',
        value: productPrice,
        items: [
            {
                item_id: productId,
                discount: product.getTradeDiscountPercent(),
                price: productPrice,
            },
        ],
    });

    // Bing UET
    window.uetq = window.uetq || [];
    window.uetq.push('event', 'add_to_cart', {
        event_category: 'Add to Cart',
        event_label: 'Add to Cart',
        ecomm_prodid: [String(productId)],
        ecomm_pagetype: getBingEcommPageType(),
        ecomm_totalvalue: totalValue,
        revenue_value: totalValue,
        currency: 'USD',
        items: [
            {
                id: String(productId),
                quantity,
                price: productPrice,
            },
        ],
    });

    logRedditEvent([
        'track',
        'AddToCart',
        {
            currency: 'USD',
            email: getEmail(),
            itemCount: quantity,
            products: [
                {
                    category: product.getTaxonomy(),
                    id: String(productId),
                    itemPrice: productPrice,
                    name: product.getTitle(),
                    quantity,
                },
            ],
            value: totalValue,
        },
    ]);

    trackAddToCartForPRT();
}

/**
 * Tracks removing a product from the cart
 *
 * @param {ProductModel} product The product that is being removed from the cart
 * @param {CheckoutPosition=} position Optional position to track where the removal happened
 */
export function trackRemoveFromCart(product, position = CheckoutPosition.CART) {
    logAmplitudeEvent('cart - remove', {
        'product id': product.getId(),
        'product taxonomy': product.getTaxonomy(),
        'position': position,
    });
}

/**
 * Tracks changes in cart item quantities
 *
 * @param {number} quantity
 * @param {ProductModel} productModel
 */
export function trackCartItemQuantity(quantity, productModel) {
    logAmplitudeEvent('cart item - quantity change', {
        [AmplitudeEventProperty.POSITION]: CheckoutPosition.MINI_CART,
        [AmplitudeEventProperty.QUANTITY]: quantity,
        [AmplitudeEventProperty.DEALER_GUID]: productModel.getDealerGuid(),
        [AmplitudeEventProperty.DISCOUNT_PERCENT]: productModel.getDiscountPercent(),
        [AmplitudeEventProperty.IS_MARKDOWN]: productModel.getIsMarkdown(),
        [AmplitudeEventProperty.IS_NEWLY_MADE]: productModel.getIsNewlyMade(),
        [AmplitudeEventProperty.IS_ON_PUBLIC_SALE]: productModel.getIsOnPublicSale(),
        [AmplitudeEventProperty.PRODUCT_ID]: productModel.getId(),
        [AmplitudeEventProperty.PRODUCT_PRICE]: productModel.getPrice(),
        [AmplitudeEventProperty.PRODUCT_TAXONOMY]: productModel.getTaxonomy(),
        [AmplitudeEventProperty.TRADE_DISCOUNT_PERCENT]: productModel.getTradeDiscountPercent(),
    });
}

/**
 * Tracks when the buyer guarantee link is clicked on the MiniCart
 *
 * @param {Event} e
 */
export function trackBuyerGuarantee(e) {
    const $link = $(e.currentTarget);

    logAmplitudeClickEvent('Buyer Guarantee', CheckoutPosition.MINI_CART, $link.attr('href'));
}
